/* Local-first store for the inbox — native IndexedDB, no dependency.
 *
 * Holds the FULL conversation list per account and the FULL message list per
 * conversation, persistently and WITHOUT the size cap localStorage had. This is
 * the on-device store the inbox renders from instantly (like Telegram reading
 * from the local DB instead of the network). Every read is wrapped so a failure
 * (private mode, quota, unsupported) degrades gracefully to "no cache" — the app
 * then behaves exactly as before (fetch from server).
 */
const DB_NAME = 'chathub_local';
const DB_VERSION = 1;
let _dbPromise = null;

function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    let req;
    try { req = indexedDB.open(DB_NAME, DB_VERSION); } catch (e) { reject(e); return; }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('convs')) db.createObjectStore('convs', { keyPath: 'account_id' });
      if (!db.objectStoreNames.contains('msgs')) db.createObjectStore('msgs', { keyPath: 'conversation_id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

function run(store, mode, fn) {
  return openDB().then((db) => new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const req = fn(t.objectStore(store));
    let out;
    if (req) req.onsuccess = () => { out = req.result; };
    t.oncomplete = () => resolve(out);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  }));
}

// ── Conversations: { account_id, list:[...] } per account ──
export async function getAllConvs() {
  try {
    const rows = await run('convs', 'readonly', (s) => s.getAll());
    const out = {};
    (rows || []).forEach((r) => { out[r.account_id] = r.list; });
    return out;
  } catch { return {}; }
}
export async function putConvs(accountId, list) {
  try { await run('convs', 'readwrite', (s) => s.put({ account_id: accountId, list })); } catch { /* ignore */ }
}

// ── Messages: { conversation_id, list:[...] } per conversation ──
export async function getMsgs(convId) {
  try { const row = await run('msgs', 'readonly', (s) => s.get(convId)); return row ? row.list : null; } catch { return null; }
}
export async function putMsgs(convId, list) {
  try { await run('msgs', 'readwrite', (s) => s.put({ conversation_id: convId, list })); } catch { /* ignore */ }
}
